import { executeRefresh } from '~/composables/useAuthRefresh'

/**
 * すべてのクライアントで共有する in-flight な refresh。
 * refresh token は rotation されるため、同時に複数 refresh を投げると
 * blacklist 済み token で 401 ループになる。ここで 1 本に束ねる。
 *
 * nuxt-auth の自動 refresh は rotation と競合するため nuxt.config で無効化し、
 * refresh はこの経路(401 リトライ)に一本化する。
 */
let inflightRefresh: Promise<boolean> | null = null

let redirecting = false

type FetchErrorLike = {
  response?: { status?: number }
  status?: number
  statusCode?: number
  name?: string
}

const isUnauthorized = (error: unknown): boolean => {
  const e = error as FetchErrorLike | null | undefined
  if (e?.name === 'FetchError') {
    return e.statusCode === 401 || e.response?.status === 401
  }
  return (
    e?.response?.status === 401 || e?.status === 401 || e?.statusCode === 401
  )
}

const redirectToSupplier = (url: string): Promise<never> => {
  if (!redirecting) {
    redirecting = true
    window.location.replace(url)
  }
  return new Promise(() => {})
}

/**
 * access token を Authorization ヘッダに載せ、401 を検知したら refresh してから
 * 1 度だけリトライする $fetch ラッパーを返す。
 *
 * token は useAuth().token(useState 由来で常に最新・"Bearer xxx" 整形済み)から取得する。
 */
export const useAuthedFetch = (
  baseURL?: string,
  staticHeaders: HeadersInit = {}
) => {
  const runtimeConfig = useRuntimeConfig()
  const client = $fetch.create({ baseURL })

  type RequestOptions = Parameters<typeof client>[1]

  const request = async <T = unknown>(
    url: string,
    options: RequestOptions = {}
  ): Promise<T> => {
    const { token, refreshToken } = useAuth()

    const withAuth = (headers?: HeadersInit): HeadersInit => {
      const mergedHeaders = new Headers(staticHeaders)
      if (headers) {
        new Headers(headers).forEach((value, key) => {
          mergedHeaders.set(key, value)
        })
      }
      if (token.value) {
        mergedHeaders.set('Authorization', token.value)
      }
      return mergedHeaders
    }

    const bailToSupplier = (cause: unknown): Promise<never> => {
      return redirectToSupplier(
        runtimeConfig.public.SUPPLIER_ORDERING_URL
      ).then(() => {
        throw cause
      })
    }

    try {
      return (await client(url, {
        ...options,
        headers: withAuth(options?.headers),
      })) as T
    } catch (error) {
      if (!isUnauthorized(error)) throw error

      if (!refreshToken.value) {
        return bailToSupplier(error)
      }

      try {
        if (!inflightRefresh) {
          inflightRefresh = executeRefresh().finally(() => {
            inflightRefresh = null
          })
        }

        const refreshed = await inflightRefresh
        if (!refreshed) {
          return bailToSupplier(error)
        }
      } catch (refreshError) {
        return bailToSupplier(refreshError)
      }

      try {
        return (await client(url, {
          ...options,
          headers: withAuth(options?.headers),
        })) as T
      } catch (retryError) {
        if (isUnauthorized(retryError)) {
          return bailToSupplier(retryError)
        }
        throw retryError
      }
    }
  }

  return { request }
}
